import { sectionTone, type SectionTone } from '../shared/section-heading'

// The Admin section's pages and their in-page sections: a leaf, so the layout's pending UI (on the
// eager path) and its tree share them without the tree's weight. Untranslated: internal copy no customer sees.

export const ADMIN_PAGES = [
  { key: 'metrics', to: '/admin/metrics', label: 'Metrics' },
  { key: 'errors', to: '/admin/errors', label: 'Errors' },
  { key: 'logs', to: '/admin/logs', label: 'Logs' },
  { key: 'traces', to: '/admin/traces', label: 'Traces' },
  { key: 'sessions', to: '/admin/sessions', label: 'Sessions' },
  { key: 'components', to: '/admin/components', label: 'Components' },
  { key: 'transitions', to: '/admin/transitions', label: 'Transitions' },
  { key: 'emails', to: '/admin/emails', label: 'Email templates' },
] as const
export type AdminPageKey = (typeof ADMIN_PAGES)[number]['key']

/** The pages that take the whole width beside the tree (their routes' `staticData.fullWidth`): a table, a playground. */
export const ADMIN_WIDE_PAGES: readonly AdminPageKey[] = [
  'errors',
  'logs',
  'traces',
  'sessions',
  'metrics',
  'transitions',
]

/** The playground pages any signed-in user may open; every other page reads super-admin-only data. */
const OPEN_ADMIN_PAGES: ReadonlySet<AdminPageKey> = new Set(['components', 'transitions'])

export const isSuperAdminPage = (pathname: string) => !OPEN_ADMIN_PAGES.has(adminPageOf(pathname))

/** The page a pathname opens; the bare `/admin` redirects to the first. */
export function adminPageOf(pathname: string): AdminPageKey {
  return ADMIN_PAGES.find((page) => pathname.includes(page.to))?.key ?? 'metrics'
}

/** The component demos' sections, in page order: their anchors, the cards' headings (component-demos.tsx) and the tree's labels. */
export const COMPONENT_DEMOS = [
  { id: 'demo-avatars', label: 'Avatars', description: 'All avatar sizes without image.' },
  { id: 'demo-toasts', label: 'Toasts', description: 'All toast variants with pill icons.' },
  {
    id: 'demo-fetching',
    label: 'Fetching progress',
    description: 'A slim progress bar that animates toward 95%, then completes and reverses out when fetching ends.',
  },
] as const

/** An email template's anchor on the gallery. */
export const emailAnchor = (id: string) => `email-${id}`

/** The playground's one hue, as the account's pages share amber: every card here carries it. */
export const ADMIN_TONE: SectionTone = sectionTone('violet')
